import React, { useState, useMemo } from 'react';
import { Plus, DollarSign, X, Check, Edit2, Trash2 } from 'lucide-react';
import { SalesGoal, Order } from './types';
import { Card, Badge } from './SharedComponents';

export const GoalsPage = ({ goals, onUpdateGoals, orders }: { goals: SalesGoal[], onUpdateGoals: (g: SalesGoal[]) => void, orders: Order[] }) => {
  const { currentMonthCapitalized, currentYear } = useMemo(() => {
    const now = new Date();
    const monthName = now.toLocaleString('pt-BR', { month: 'long' });
    return {
      currentMonthCapitalized: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      currentYear: now.getFullYear()
    };
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<SalesGoal | null>(null);
  const [newGoal, setNewGoal] = useState({ month: 'Março', year: 2025, target: '' });
  
  const [goalToDelete, setGoalToDelete] = useState<string | null>(null);

  const faturamentoReal = orders.reduce((acc, o) => acc + (o.estimatedCost || 0), 0);

  const handleOpenAdd = () => {
    setEditingGoal(null);
    setNewGoal({ month: currentMonthCapitalized, year: currentYear, target: '' });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (goal: SalesGoal) => {
    setEditingGoal(goal);
    setNewGoal({ month: goal.month, year: goal.year, target: goal.targetValue.toString() });
    setIsModalOpen(true);
  };

  const executeDelete = () => {
    if (goalToDelete) {
      onUpdateGoals(goals.filter(g => g.id !== goalToDelete));
      setGoalToDelete(null);
    }
  };

  const parseTargetValue = (val: string) => {
    const cleanValue = val.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  const handleSaveGoal = () => {
    const targetValue = parseTargetValue(newGoal.target);
    
    if (editingGoal) {
      const updated = goals.map(g => g.id === editingGoal.id ? { 
        ...g, 
        month: newGoal.month, 
        year: newGoal.year, 
        targetValue: targetValue
      } : g);
      onUpdateGoals(updated);
    } else {
      const goal: SalesGoal = {
        id: Date.now().toString(),
        month: newGoal.month,
        year: newGoal.year,
        targetValue: targetValue,
        currentValue: 0,
        status: 'ACTIVE'
      };
      
      const updatedGoals = goals.map(g => ({ ...g, status: 'COMPLETED' as any }));
      onUpdateGoals([...updatedGoals, goal]);
    }
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20 p-4 md:p-8 lg:p-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6">
        <div className="space-y-1">
          <h1 className="text-2xl md:text-3xl font-black text-secondary uppercase tracking-tighter">Metas de Vendas</h1>
          <p className="text-gray-400 text-xs md:text-sm font-medium">Definição estratégica de faturamento para a unidade.</p>
        </div>
        <button 
          onClick={handleOpenAdd}
          className="w-full sm:w-auto bg-primary text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2"
        >
          <Plus size={18} /> Nova Meta Mensal
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
        {goals.map(goal => {
          const isCurrentMonth = goal.month === currentMonthCapitalized && goal.year === currentYear;
          const progress = Math.min(Math.round((faturamentoReal / goal.targetValue) * 100), 100);
          
          return (
            <Card 
              key={goal.id} 
              className={`p-6 md:p-10 bg-white border-2 transition-all relative group h-full flex flex-col justify-between ${
                isCurrentMonth 
                  ? 'border-primary shadow-2xl scale-[1.01] ring-4 ring-primary/5' 
                  : 'border-gray-100 opacity-90'
              }`}
            >
               {isCurrentMonth && (
                 <div className="absolute -top-3 left-6 md:left-10 bg-primary text-white px-4 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shadow-lg animate-pulse">
                    Meta Vigente
                 </div>
               )}

               <div className="flex justify-between items-start mb-8 md:mb-10">
                  <div>
                    <h3 className={`text-xl md:text-2xl font-black uppercase tracking-tight ${isCurrentMonth ? 'text-primary' : 'text-secondary'}`}>
                      {goal.month} {goal.year}
                    </h3>
                    <Badge className={goal.status === 'ACTIVE' ? 'bg-primary text-white border-none mt-2' : 'bg-gray-100 text-gray-400 border-none mt-2'}>
                       {goal.status === 'ACTIVE' ? 'META ATIVA' : 'HISTÓRICO'}
                    </Badge>
                  </div>
                  <div className="flex gap-1.5">
                     <button onClick={() => handleOpenEdit(goal)} className="p-2.5 bg-gray-50 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-xl transition-all shadow-sm"><Edit2 size={14} /></button>
                     <button onClick={() => setGoalToDelete(goal.id)} className="p-2.5 bg-gray-50 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all shadow-sm"><Trash2 size={14} /></button>
                  </div>
               </div>

               <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
                     <div className="space-y-1">
                        <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">Atingido</p>
                        <p className="text-2xl md:text-4xl font-black text-secondary tracking-tighter">R$ {faturamentoReal.toLocaleString('pt-BR')}</p>
                     </div>
                     <div className="text-left sm:text-right space-y-1">
                        <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">Objetivo</p>
                        <p className="text-lg md:text-2xl font-black text-primary tracking-tighter">R$ {goal.targetValue.toLocaleString('pt-BR')}</p>
                     </div>
                  </div>

                  <div className="relative pt-2">
                    <div className="w-full bg-gray-100 h-5 md:h-6 rounded-full overflow-hidden shadow-inner border border-gray-50">
                       <div 
                        className={`h-full transition-all duration-1000 ease-out flex items-center justify-end px-3 ${progress >= 100 ? 'bg-green-500' : 'bg-primary shadow-[0_0_15px_rgba(0,168,224,0.3)]'}`} 
                        style={{ width: `${progress}%` }}
                       >
                          <span className="text-[8px] md:text-[10px] font-black text-white">{progress}%</span>
                       </div>
                    </div>
                  </div>
               </div>
            </Card>
          );
        })}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-secondary/80 backdrop-blur-sm z-[200] flex items-center justify-center p-6">
           <div className="bg-white rounded-[40px] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95">
              <div className="p-8 bg-primary text-white flex justify-between items-center">
                 <h2 className="text-2xl font-black uppercase tracking-tight">{editingGoal ? 'Editar Meta' : 'Configurar Meta'}</h2>
                 <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-white/10 rounded-xl transition-all"><X size={32} /></button>
              </div>
              <div className="p-10 space-y-8">
                 <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Mês</label>
                       <select className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary" value={newGoal.month} onChange={e => setNewGoal({...newGoal, month: e.target.value})}>
                          <option>Janeiro</option><option>Fevereiro</option><option>Março</option><option>Abril</option><option>Maio</option><option>Junho</option>
                          <option>Julho</option><option>Agosto</option><option>Setembro</option><option>Outubro</option><option>Novembro</option><option>Dezembro</option>
                       </select>
                    </div>
                    <div className="space-y-1.5">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Ano</label>
                       <input type="number" className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary" value={newGoal.year} onChange={e => setNewGoal({...newGoal, year: parseInt(e.target.value)})} />
                    </div>
                 </div>
                 
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Valor Alvo (R$)</label>
                    <div className="relative">
                       <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" size={22} />
                       <input 
                        required
                        autoFocus
                        type="text"
                        className="w-full bg-gray-50 border border-gray-100 p-5 pl-14 rounded-[28px] outline-none font-black text-secondary text-2xl focus:border-primary transition-all shadow-inner" 
                        placeholder="Ex: 70.000" 
                        value={newGoal.target} 
                        onChange={e => setNewGoal({...newGoal, target: e.target.value})} 
                       />
                    </div>
                    <p className="text-[9px] text-gray-400 px-4">Dica: Você pode digitar com ou sem pontos.</p>
                 </div>

                 <button onClick={handleSaveGoal} className="w-full py-6 bg-primary text-white rounded-[32px] font-black uppercase text-sm tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-3">
                    <Check size={24} /> {editingGoal ? 'Salvar Alterações' : 'Ativar Meta'}
                 </button>
              </div>
           </div>
        </div>
      )}

      {goalToDelete && (
        <div className="fixed inset-0 bg-secondary/90 backdrop-blur-md z-[300] flex items-center justify-center p-6 animate-in fade-in">
           <div className="bg-white rounded-[48px] w-full max-w-sm shadow-2xl p-10 text-center animate-in zoom-in-95">
              <div className="w-24 h-24 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                 <Trash2 size={40} />
              </div>
              <h3 className="text-2xl font-black text-secondary uppercase tracking-tight italic">Excluir Meta?</h3>
              <p className="text-sm text-gray-400 font-medium leading-relaxed mt-2 mb-8">Esta ação irá remover o registro histórico de metas do banco de dados.</p>
              
              <div className="flex flex-col gap-3">
                 <button onClick={executeDelete} className="w-full py-5 bg-red-500 text-white rounded-[24px] font-black uppercase text-xs tracking-widest shadow-xl shadow-red-100 active:scale-95 transition-all">Confirmar Exclusão</button>
                 <button onClick={() => setGoalToDelete(null)} className="w-full py-4 text-[10px] font-black uppercase text-gray-400 hover:text-secondary">Cancelar</button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};
